import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Target, 
  Eye, 
  Heart, 
  Award, 
  Users, 
  Globe, 
  TrendingUp, 
  Shield,
  MapPin,
  Phone,
  Mail,
  ArrowRight
} from 'lucide-react';

const About = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const values = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: 'Client Success First',
      description: 'Every decision we make is guided by what\'s best for our clients. Their success is our success.'
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Excellence in Execution',
      description: 'We hold ourselves to the highest standards of quality and professionalism.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Integrity Always',
      description: 'We do what\'s right, even when no one is watching. Transparency is non-negotiable.'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Continuous Innovation',
      description: 'Technology never stands still, and neither do we. We\'re constantly learning and improving.'
    }
  ];

  const differentiators = [
    {
      title: 'Deep Expertise',
      description: 'Our team brings an average of 10+ years of experience across enterprise technologies.'
    },
    {
      title: 'Platform Certifications',
      description: 'Certified consultants for Oracle, SAP, Salesforce, and Microsoft platforms.'
    },
    {
      title: 'Industry Knowledge',
      description: 'Domain expertise that accelerates project success across 15+ industries.'
    },
    {
      title: 'Flexible Engagement',
      description: 'Models that fit your needs, from single consultants to full project teams.'
    },
    {
      title: 'Proven Track Record',
      description: '250+ projects delivered with a 98% client retention rate.'
    },
    {
      title: 'End-to-End Capabilities',
      description: 'Comprehensive services from strategy to implementation to ongoing support.'
    }
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 gradient-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">About Us</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Building Tomorrow's Technology Today
            </h1>
            <p className="text-lg text-white/80">
              TeqDeepSeek Solutions is a premium technology partner committed to helping 
              enterprises in Dubai and India achieve digital excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-accent font-semibold text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-6">
                From Vision to Reality
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  Founded in 2023 and officially established in 2026, TeqDeepSeek Solutions 
                  was born from a simple belief: enterprises deserve technology partners who 
                  understand both the technical and business dimensions of digital transformation.
                </p>
                <p>
                  What started as a small team of passionate technologists has grown into a 
                  50+ person organization with delivery centers in Dubai and India. Along the 
                  way, we've helped over 100 enterprise clients navigate complex technology 
                  challenges and emerge stronger.
                </p>
                <p>
                  Our journey has been defined by a relentless focus on client success. We don't 
                  measure our achievements by projects completed or hours billed—we measure them 
                  by the business value we create for our clients.
                </p>
                <p>
                  Today, we stand as a premium technology partner for enterprises in Dubai, India, 
                  and beyond. Our global delivery model combines the cost advantages of offshore 
                  development with the responsiveness of local presence.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-accent/10 rounded-2xl p-6">
                    <div className="text-4xl font-bold text-accent mb-2">50+</div>
                    <p className="text-slate-600">Technology Experts</p>
                  </div>
                  <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=300&fit=crop"
                    alt="Team collaboration"
                    className="rounded-2xl w-full h-48 object-cover"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img
                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=300&fit=crop"
                    alt="Office workspace"
                    className="rounded-2xl w-full h-48 object-cover"
                  />
                  <div className="bg-teal/10 rounded-2xl p-6">
                    <div className="text-4xl font-bold text-teal mb-2">2</div>
                    <p className="text-slate-600">Global Offices</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center text-accent mb-6">
                <Target className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-primary mb-4">Our Mission</h3>
              <p className="text-slate-600">
                To empower enterprises with technology solutions that drive measurable business 
                outcomes. We combine technical excellence with strategic insight to help our 
                clients navigate digital transformation with confidence.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center text-teal mb-6">
                <Eye className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-primary mb-4">Our Vision</h3>
              <p className="text-slate-600">
                To be the most trusted technology partner for enterprises in the Middle East 
                and India—known for our expertise, integrity, and unwavering commitment to 
                client success.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              What We Stand For
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center p-6"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-primary mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Partner With Us */}
      <section className="section gradient-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Why Us</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4">
              Why Partner With TeqDeepSeek?
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {differentiators.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="p-6 bg-white/10 backdrop-blur-sm rounded-xl"
              >
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-semibold text-sm uppercase tracking-wider">Locations</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Global Presence
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our dual-presence model combines local market understanding with cost-effective 
              global delivery.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Dubai Office */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center">
                  <Globe className="w-8 h-8 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary">Dubai Office</h3>
                  <p className="text-slate-500">Headquarters</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <p className="text-slate-600">Business Bay, Dubai, United Arab Emirates</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                  <p className="text-slate-600">+971-4-XXX-XXXX</p>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                  <p className="text-slate-600">dubai@teqdeepseek.com</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  <strong className="text-primary">Coverage:</strong> Middle East and North Africa region
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  <strong className="text-primary">Specialization:</strong> Client engagement, project management, local market expertise
                </p>
              </div>
            </motion.div>

            {/* India Office */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal/10 rounded-xl flex items-center justify-center">
                  <Users className="w-8 h-8 text-teal" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-primary">India Delivery Center</h3>
                  <p className="text-slate-500">Development Hub</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <p className="text-slate-600">[City], India</p>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-teal flex-shrink-0" />
                  <p className="text-slate-600">+91-XX-XXXX-XXXX</p>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-teal flex-shrink-0" />
                  <p className="text-slate-600">india@teqdeepseek.com</p>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  <strong className="text-primary">Coverage:</strong> Global delivery, offshore development
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  <strong className="text-primary">Specialization:</strong> Development, testing, 24/7 support
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Let's Build Something Great Together
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Ready to start your digital transformation journey? We're here to help.
            </p>
            <Link to="/contact" className="btn bg-white text-accent hover:bg-slate-100 btn-lg">
              Get in Touch
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
